import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { FixedBidQuoteLines, MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { fixedBidQuote, markupPercentOf } from '../utils/fixedBidMarkup';

interface EditProposalDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId: string;
  accessToken: string;
  onSuccess: () => void;
}

interface ProposalFormData {
  name: string;
  description: string;
  status: string;
  pricingType: string;
  /** On a fixed bid, the price before markup. */
  amount: number;
  /** A fixed bid's markup (contingency buffer); the saved amount is the bid total. */
  markupPercent: MarkupInput;
  hourlyRate: number;
  estimatedHours: number;
  startDate: string;
  endDate: string;
  deliverables: Array<{
    id: string;
    name: string;
    description: string;
    dueDate: string;
    status: string;
  }>;
}

export function EditProposalDialog({ open, proposalId, onClose, accessToken, onSuccess }: EditProposalDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ProposalFormData>({
    name: '',
    description: '',
    status: 'draft',
    pricingType: 'fixed',
    amount: 0,
    markupPercent: 0,
    hourlyRate: 0,
    estimatedHours: 0,
    startDate: '',
    endDate: '',
    deliverables: [],
  });
  const quote = fixedBidQuote(formData.amount, formData.markupPercent);

  useEffect(() => {
    if (open && proposalId) {
      loadProposal();
    }
  }, [open, proposalId]);

  const loadProposal = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${proposalId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const proposal = data.sow;
        
        setFormData({
          name: proposal.name || '',
          description: proposal.description || '',
          status: proposal.status || 'draft',
          pricingType: proposal.pricing?.type || 'fixed',
          // A saved markup means `amount` is the bid total: edit the base, show the markup.
          amount: proposal.pricing?.basePrice ?? proposal.pricing?.amount ?? 0,
          markupPercent: markupPercentOf(proposal.pricing?.markupPercent),
          hourlyRate: proposal.pricing?.hourlyRate || 0,
          estimatedHours: proposal.pricing?.estimatedHours || 0,
          startDate: proposal.timeline?.startDate?.split('T')[0] || '',
          endDate: proposal.timeline?.endDate?.split('T')[0] || '',
          deliverables: proposal.deliverables || [],
        });
      } else {
        toast.error('Failed to load proposal');
        onClose();
      }
    } catch (error) {
      console.error('Error loading proposal:', error);
      toast.error('Failed to load proposal');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the proposal');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${proposalId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            status: formData.status,
            pricing: {
              type: formData.pricingType,
              amount: formData.pricingType === 'fixed' ? quote.total : formData.amount,
              hourlyRate: formData.hourlyRate,
              estimatedHours: formData.estimatedHours,
              ...(formData.pricingType === 'fixed' ? { basePrice: quote.base, markupPercent: quote.percent, markupAmount: quote.markup } : {}),
            },
            timeline: {
              startDate: formData.startDate,
              endDate: formData.endDate,
            },
            deliverables: formData.deliverables,
          }),
        }
      );

      if (response.ok) {
        toast.success('Proposal updated');
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update proposal');
      }
    } catch (error) {
      console.error('Error updating proposal:', error);
      toast.error('Failed to update proposal');
    } finally {
      setSaving(false);
    }
  };

  const addDeliverable = () => {
    setFormData({
      ...formData,
      deliverables: [
        ...formData.deliverables,
        {
          id: `del-${Date.now()}`,
          name: '',
          description: '',
          dueDate: formData.endDate || new Date().toISOString().split('T')[0],
          status: 'pending',
        },
      ],
    });
  };

  const removeDeliverable = (index: number) => {
    setFormData({
      ...formData,
      deliverables: formData.deliverables.filter((_, i) => i !== index),
    });
  };

  const updateDeliverable = (index: number, field: string, value: string) => {
    const newDeliverables = [...formData.deliverables];
    newDeliverables[index] = { ...newDeliverables[index], [field]: value };
    setFormData({
      ...formData,
      deliverables: newDeliverables,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Proposal</DialogTitle>
          <DialogDescription>
            Update proposal details and pricing
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Proposal Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Q1 2025 Implementation Proposal"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the proposal scope..."
                  rows={6}
                />
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="pricingType">Pricing Model</Label>
                <Select value={formData.pricingType} onValueChange={(value) => setFormData({ ...formData, pricingType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                    <SelectItem value="hourly">Hourly Rate</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.pricingType === 'fixed' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount">Fixed Price ($)</Label>
                      <Input
                        id="amount"
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                        placeholder="75000"
                      />
                    </div>
                    <MarkupPercentField id="proposal-markup" value={formData.markupPercent} onChange={(v) => setFormData({ ...formData, markupPercent: v })} />
                  </div>
                  {quote.percent > 0 && <FixedBidQuoteLines quote={quote} testId="proposal-fixed-bid-quote" className="text-sm rounded-md border p-3" />}
                </div>
              )}

              {formData.pricingType === 'hourly' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      value={formData.hourlyRate}
                      onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || 0 })}
                      placeholder="150"
                    />
                  </div>
                  <div>
                    <Label htmlFor="estimatedHours">Estimated Hours</Label>
                    <Input
                      id="estimatedHours"
                      type="number"
                      value={formData.estimatedHours}
                      onChange={(e) => setFormData({ ...formData, estimatedHours: parseFloat(e.target.value) || 0 })}
                      placeholder="500"
                    />
                  </div>
                </div>
              )}

              {formData.pricingType === 'hybrid' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="amount">Base Fixed Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                      placeholder="50000"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                      <Input
                        id="hourlyRate"
                        type="number"
                        value={formData.hourlyRate}
                        onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || 0 })}
                        placeholder="150"
                      />
                    </div>
                    <div>
                      <Label htmlFor="estimatedHours">Additional Hours</Label>
                      <Input
                        id="estimatedHours"
                        type="number"
                        value={formData.estimatedHours}
                        onChange={(e) => setFormData({ ...formData, estimatedHours: parseFloat(e.target.value) || 0 })}
                        placeholder="100"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Deliverables */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Deliverables</Label>
                <Button type="button" size="sm" variant="outline" onClick={addDeliverable}>
                  <Plus className="size-4 mr-2" />
                  Add Deliverable
                </Button>
              </div>

              <div className="space-y-3">
                {formData.deliverables.map((deliverable, index) => (
                  <div key={deliverable.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Input
                        value={deliverable.name}
                        onChange={(e) => updateDeliverable(index, 'name', e.target.value)}
                        placeholder="Deliverable name"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeDeliverable(index)}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                    <Textarea
                      value={deliverable.description}
                      onChange={(e) => updateDeliverable(index, 'description', e.target.value)}
                      placeholder="Description"
                      rows={2}
                    />
                    <Input
                      type="date"
                      value={deliverable.dueDate}
                      onChange={(e) => updateDeliverable(index, 'dueDate', e.target.value)}
                    />
                  </div>
                ))}

                {formData.deliverables.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No deliverables added yet
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
            Save Proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}